<script setup>
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUIStore } from '@/core/stores/ui.store'
import NewBradleyHome from './new-bradley-home/new-bradley-home.vue'

const { t } = useI18n()
const uiStore = useUIStore()
const { bradleyPanelOpen, bradleyPanelWidthPercent, bradleyPanelExpanded, bradleyFloatingWidgetVisible } = storeToRefs(uiStore)

const isResizing = ref(false)
const resizeHandlers = ref({ onMouseMove: null, onMouseUp: null })

const shouldMountBradleyHome = computed(() => (
  bradleyPanelOpen.value || bradleyFloatingWidgetVisible.value
))

const panelWidthStyle = computed(() => ({
  width: bradleyPanelExpanded.value ? '100%' : `${bradleyPanelWidthPercent.value}vw`,
}))

function onResizeStart(event) {
  event.preventDefault()
  isResizing.value = true
  const startX = event.clientX
  const startPercent = bradleyPanelWidthPercent.value

  function onMouseMove(moveEvent) {
    const windowWidth = window.innerWidth
    const deltaPx = startX - moveEvent.clientX
    const deltaPercent = (deltaPx / windowWidth) * 100
    uiStore.setBradleyPanelWidth(startPercent + deltaPercent)
  }

  function onMouseUp() {
    isResizing.value = false
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    resizeHandlers.value = { onMouseMove: null, onMouseUp: null }
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  resizeHandlers.value = { onMouseMove, onMouseUp }
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

function cleanupResize() {
  const { onMouseMove, onMouseUp } = resizeHandlers.value
  if (onMouseMove) {
    document.removeEventListener('mousemove', onMouseMove)
  }
  if (onMouseUp) {
    document.removeEventListener('mouseup', onMouseUp)
  }
  resizeHandlers.value = { onMouseMove: null, onMouseUp: null }
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

watch(
  bradleyPanelOpen,
  (open) => {
    if (typeof document === 'undefined')
      return
    document.body.style.overflow = open ? 'hidden' : ''
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  cleanupResize()
  if (typeof document !== 'undefined')
    document.body.style.overflow = ''
})
</script>

<template>
  <teleport to="body">
    <div
      class="bradley-side-panel-overlay"
      :class="{ 'bradley-side-panel-overlay--hidden': !bradleyPanelOpen }"
      aria-label="Bradley AI Assistant panel"
      :aria-hidden="!bradleyPanelOpen"
    >
      <div
        class="bradley-side-panel-backdrop"
        aria-hidden="true"
        @click="uiStore.minimizeBradleyPanel()"
      />
      <div
        class="bradley-side-panel"
        :class="{ 'bradley-side-panel--resizing': isResizing }"
        :style="panelWidthStyle"
      >
        <div
          v-if="!bradleyPanelExpanded"
          class="bradley-side-panel__resize-handle"
          @mousedown="onResizeStart"
        >
          <hr
            class="bradley-side-panel__resize-handle-line"
            :aria-label="t('newBradley.panel.resizeHandle')"
          >
        </div>
        <div class="bradley-side-panel__content">
          <div class="bradley-side-panel__body">
            <new-bradley-home
              v-if="shouldMountBradleyHome"
              embedded
            />
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
.bradley-side-panel-overlay {
  position: fixed;
  top: calc(86px + 17px);
  right: 17px;
  bottom: 17px;
  left: 17px;
  z-index: 50;
  pointer-events: auto;
  box-sizing: border-box;
  min-height: 0;
  transition: opacity 0.2s ease-out, visibility 0.2s ease-out;
}

.bradley-side-panel-overlay--hidden {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.bradley-side-panel-backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  cursor: pointer;
}

.bradley-side-panel {
  position: relative;
  z-index: 1;
  pointer-events: auto;
  margin-left: auto;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(25, 26, 55, 0.12), 0 2px 8px rgba(25, 26, 55, 0.08);
  transition: width 0.2s ease-out;
  overflow: hidden;
}

.bradley-side-panel--resizing {
  transition: none;
}

.bradley-side-panel__resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 20;
}

.bradley-side-panel__resize-handle:hover {
  background: rgba(25, 26, 55, 0.06);
}

.bradley-side-panel__resize-handle-line {
  margin: 0;
  padding: 0;
  border: none;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

.bradley-side-panel__content {
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.bradley-side-panel__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>

<style>
html.dark .bradley-side-panel {
  background: #14143a;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.35);
}

html.dark .bradley-side-panel__resize-handle:hover {
  background: rgba(255, 255, 255, 0.08);
}
</style>
